import type { SocialChannel } from './types';
import type { ClubTheme } from './themes';
import { useJoinChannel, useLeaveChannel } from './queries';

interface ChannelSidebarProps {
  channels: SocialChannel[];
  activeSlug: string | null;
  onSelect: (slug: string | null) => void;
  theme: ClubTheme;
  clubId: string;
}

export default function ChannelSidebar({ channels, activeSlug, onSelect, theme, clubId }: ChannelSidebarProps) {
  const joinChannel = useJoinChannel(clubId);
  const leaveChannel = useLeaveChannel(clubId);

  const handleToggle = (ch: SocialChannel, e: React.MouseEvent) => {
    e.stopPropagation();
    if (ch.is_joined) {
      leaveChannel.mutate(ch.id);
    } else {
      joinChannel.mutate(ch.id);
    }
  };

  return (
    <nav className="space-y-1">
      {/* All posts */}
      <button
        type="button"
        onClick={() => onSelect(null)}
        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors text-left"
        style={{
          backgroundColor: activeSlug === null ? `${theme.colors.primary}20` : 'transparent',
          color: activeSlug === null ? theme.colors.primary : theme.colors.text,
        }}
      >
        All Posts
      </button>

      {channels.map((ch) => (
        <div
          key={ch.slug}
          className="group flex items-center rounded-xl transition-colors"
          style={{
            backgroundColor: activeSlug === ch.slug ? `${theme.colors.primary}20` : 'transparent',
          }}
        >
          <button
            type="button"
            onClick={() => onSelect(ch.slug)}
            className="flex flex-1 items-center gap-2 px-3 py-2 text-sm transition-colors text-left min-w-0"
            style={{
              color: activeSlug === ch.slug ? theme.colors.primary : theme.colors.muted,
            }}
          >
            <span className="text-xs opacity-60">#</span>
            <span className="truncate">{ch.name}</span>
            {ch.is_locked && <span className="text-[10px]">🔒</span>}
          </button>

          {/* Join/leave toggle — only for non-locked, non-default channels */}
          {!ch.is_locked && !ch.is_default && (
            <button
              type="button"
              onClick={(e) => handleToggle(ch, e)}
              disabled={joinChannel.isPending || leaveChannel.isPending}
              title={ch.is_joined ? 'Leave channel' : 'Join channel'}
              className="mr-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium opacity-0 transition-opacity group-hover:opacity-100 disabled:opacity-30"
              style={{
                backgroundColor: ch.is_joined ? `${theme.colors.muted}20` : `${theme.colors.primary}20`,
                color: ch.is_joined ? theme.colors.muted : theme.colors.primary,
              }}
            >
              {ch.is_joined ? 'Leave' : 'Join'}
            </button>
          )}

          {/* Joined indicator for non-default channels */}
          {!ch.is_locked && !ch.is_default && ch.is_joined && (
            <span
              className="mr-2 h-1.5 w-1.5 shrink-0 rounded-full group-hover:hidden"
              style={{ backgroundColor: theme.colors.primary }}
            />
          )}
        </div>
      ))}
    </nav>
  );
}
